/**
 * @fileoverview The one thing a reader sees when a page does not load.
 *
 * THERE IS EXACTLY ONE OF THESE AND IT TAKES NO PROPS. Missing, malformed,
 * unreachable, tampered with, or encrypted and not for you: all the same words,
 * because a private page must be indistinguishable from a page that was never
 * published. A `reason` prop, however carefully worded, is the thing a later
 * change would surface -- so there is not one to surface.
 *
 * The copy avoids "you do not have access", which would confirm there is
 * something to have access to.
 *
 * ONE LINE DEPENDS ON THE READER, NEVER ON THE PAGE. A signed-out reader is told
 * that a page shared with them opens once they sign in. Every address shows it
 * alike, so it says nothing about whether this one exists. It is there because
 * an extension sign-in is not restored across a reload (deliberate in shared
 * auth: a page load never touches window.nostr), and a recipient revisiting a
 * link lands here signed out with nothing suggesting what to do. If their last
 * sign-in was the extension, they also get it back in one click; that too
 * depends only on the reader.
 */

import { useNostrAuth } from '@cloistr/auth';

import { ContinueWithExtension } from './ContinueWithExtension';

export function PageUnavailable() {
  const { authState } = useNostrAuth();
  return (
    <div className="page-unavailable" role="status">
      <h1>This page is not available</h1>
      <p>
        Nothing readable was found at this address. It may never have existed, it may have
        been replaced, or its content may not be readable from here.
      </p>
      <p className="page-unavailable-note">
        Pages does not report why. A page that is private to someone else looks the same as
        one that does not exist, deliberately.
      </p>
      {!authState.pubkey && (
        <p className="page-unavailable-note">
          If this page was shared with you, sign in with the key it was shared to.
        </p>
      )}
      <ContinueWithExtension />
    </div>
  );
}
